import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
    Box,
    Card,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
    InputAdornment,
    CircularProgress,
    Select,
    MenuItem,
    FormControl,
    IconButton,
    Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import FirstPageIcon from "@mui/icons-material/FirstPage";
import LastPageIcon from "@mui/icons-material/LastPage";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import EmptyData from "components/EmptyData";
import apiClient from "../../Utils/apiClient";
import socketService from "../../socket/SocketService";

const STATUS_CONFIG = {
    PENDING:        { bg: "#FFF8E7", color: "#B76E00", dot: "#F5A623" },
    ACTIVE:         { bg: "#E8F5FF", color: "#0057B8", dot: "#2196F3" },
    COMPLETED:      { bg: "#E8F9F0", color: "#1A7A45", dot: "#4CAF50" },
    CANCELLED:      { bg: "#FFF0F0", color: "#C0392B", dot: "#F44336" },
    ADMIN_APPROVED: { bg: "#E8F9F0", color: "#1A7A45", dot: "#4CAF50" },
    REJECTED:       { bg: "#FFF0F0", color: "#C0392B", dot: "#F44336" },
};

const StatusBadge = ({ status }) => {
    const key = (status || "").toUpperCase();
    const config = STATUS_CONFIG[key] || { bg: "#F5F5F5", color: "#666", dot: "#999" };
    return (
        <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "6px",
            px: "10px", py: "4px", borderRadius: "20px",
            backgroundColor: config.bg, color: config.color,
            fontSize: "11px", fontWeight: 700, letterSpacing: "0.5px",
            textTransform: "uppercase", whiteSpace: "nowrap",
        }}>
            <Box sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: config.dot, flexShrink: 0 }} />
            {status || "N/A"}
        </Box>
    );
};

const ActionButtons = ({ cargo, onStatusChange }) => {
    const [loadingApprove, setLoadingApprove] = useState(false);
    const [loadingReject, setLoadingReject]   = useState(false);

    const statusUp = (cargo.status || "").toUpperCase();
    const isFinalized = ["ADMIN_APPROVED", "REJECTED", "COMPLETED", "CANCELLED"].includes(statusUp);

    const handleApprove = async () => {
        if (loadingApprove || loadingReject) return;
        setLoadingApprove(true);
        try {
            await apiClient.put(`/cargo-escort/update-status/${cargo._id}`, { status: "ADMIN_APPROVED" });
            onStatusChange(cargo._id, "ADMIN_APPROVED");
        } catch {
            alert("Failed to approve. Please try again.");
        } finally {
            setLoadingApprove(false);
        }
    };

    const handleReject = async () => {
        if (loadingApprove || loadingReject) return;
        setLoadingReject(true);
        try {
            await apiClient.put(`/cargo-escort/update-status/${cargo._id}`, { status: "REJECTED" });
            onStatusChange(cargo._id, "REJECTED");
        } catch {
            alert("Failed to reject. Please try again.");
        } finally {
            setLoadingReject(false);
        }
    };

    if (isFinalized) return <Typography sx={{ fontSize: "11px", color: "#aaa", fontStyle: "italic" }}>—</Typography>;

    return (
        <Box display="flex" gap={1} alignItems="center">
            <Box onClick={!loadingApprove && !loadingReject ? handleApprove : undefined} sx={{
                display: "inline-flex", alignItems: "center", gap: "4px",
                px: "10px", py: "5px", borderRadius: "8px",
                backgroundColor: "#f0faf4", color: "#1a7a45", border: "1px solid #b7e4c7",
                fontSize: "11px", fontWeight: 700,
                cursor: loadingApprove || loadingReject ? "not-allowed" : "pointer",
                whiteSpace: "nowrap", transition: "all 0.18s", userSelect: "none",
                opacity: loadingReject ? 0.5 : 1,
                "&:hover": !loadingApprove && !loadingReject ? { backgroundColor: "#d3f0e0", borderColor: "#52b788" } : {},
            }}>
                {loadingApprove ? <CircularProgress size={11} sx={{ color: "#1a7a45" }} /> : <CheckCircleOutlineIcon sx={{ fontSize: 13 }} />}
                Approve
            </Box>
            <Box onClick={!loadingApprove && !loadingReject ? handleReject : undefined} sx={{
                display: "inline-flex", alignItems: "center", gap: "4px",
                px: "10px", py: "5px", borderRadius: "8px",
                backgroundColor: "#fff5f5", color: "#c0392b", border: "1px solid #f5b7b1",
                fontSize: "11px", fontWeight: 700,
                cursor: loadingApprove || loadingReject ? "not-allowed" : "pointer",
                whiteSpace: "nowrap", transition: "all 0.18s", userSelect: "none",
                opacity: loadingApprove ? 0.5 : 1,
                "&:hover": !loadingApprove && !loadingReject ? { backgroundColor: "#fde8e8", borderColor: "#e74c3c" } : {},
            }}>
                {loadingReject ? <CircularProgress size={11} sx={{ color: "#c0392b" }} /> : <CancelOutlinedIcon sx={{ fontSize: 13 }} />}
                Reject
            </Box>
        </Box>
    );
};

const navBtnSx = (disabled) => ({
    width: 32, height: 32, borderRadius: "8px",
    border: "1px solid #e0e0e0",
    backgroundColor: disabled ? "#fafafa" : "#fff",
    color: disabled ? "#ccc" : "#555",
    transition: "all 0.15s",
    "&:hover": !disabled ? { backgroundColor: "#f0f4ff", borderColor: "#c5d3f5", color: "#1976d2" } : {},
});

const PaginationBar = ({ currentPage, totalPages, totalCount, limit, onPageChange, onLimitChange }) => {
    const from = totalCount === 0 ? 0 : (currentPage - 1) * limit + 1;
    const to   = Math.min(currentPage * limit, totalCount);

    const pageNumbers = useMemo(() => {
        if (totalPages <= 1) return [1];
        const delta = 1;
        const range = [];

        for (
            let i = Math.max(2, currentPage - delta);
            i <= Math.min(totalPages - 1, currentPage + delta);
            i++
        ) range.push(i);

        if (currentPage - delta > 2)           range.unshift("...");
        if (currentPage + delta < totalPages - 1) range.push("...");

        range.unshift(1);
        if (totalPages > 1) range.push(totalPages);

        return range;
    }, [currentPage, totalPages]);

    return (
        <Box
            sx={{
                display: "flex", flexWrap: "wrap",
                alignItems: "center", justifyContent: "space-between",
                gap: 2, mt: "16px", pt: "14px",
                borderTop: "1px solid #f0f0f0",
                px: 1,
            }}
        >
            {/* Left: record count + rows-per-page */}
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
                <Typography sx={{ fontSize: "13px", color: "#666", whiteSpace: "nowrap" }}>
                    Showing{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: "#333" }}>{from}–{to}</Box>
                    {" "}of{" "}
                    <Box component="span" sx={{ fontWeight: 700, color: "#333" }}>{totalCount}</Box>
                    {" "}records
                </Typography>

                <Box display="flex" alignItems="center" gap="6px">
                    <Typography sx={{ fontSize: "12px", color: "#999", whiteSpace: "nowrap" }}>
                        Rows per page:
                    </Typography>
                    <FormControl size="small" variant="outlined">
                        <Select
                            value={limit}
                            onChange={(e) => onLimitChange(Number(e.target.value))}
                            sx={{
                                fontSize: "12px",
                                height: "30px",
                                minWidth: "64px",
                                borderRadius: "8px",
                                "& .MuiOutlinedInput-notchedOutline": { borderColor: "#e0e0e0" },
                                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#bbb" },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1976d2", borderWidth: "1px" },
                                "& .MuiSelect-select": { py: "4px", px: "10px" },
                            }}
                        >
                            {[5, 10, 20, 50].map((n) => (
                                <MenuItem key={n} value={n} sx={{ fontSize: "12px" }}>{n}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </Box>

            {/* Right: page number controls */}
            <Box display="flex" alignItems="center" gap="4px">
                {/* First page */}
                <Tooltip title="First page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === 1} onClick={() => onPageChange(1)} sx={navBtnSx(currentPage === 1)}>
                            <FirstPageIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>

                {/* Prev */}
                <Tooltip title="Previous page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} sx={navBtnSx(currentPage === 1)}>
                            <ChevronLeftIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>

                {/* Page numbers */}
                {pageNumbers.map((p, i) =>
                    p === "..." ? (
                        <Typography key={`dots-${i}`} sx={{ fontSize: "13px", color: "#ccc", px: "2px", lineHeight: "32px", userSelect: "none" }}>
                            …
                        </Typography>
                    ) : (
                        <Box
                            key={p}
                            onClick={() => p !== currentPage && onPageChange(p)}
                            sx={{
                                width: 32, height: 32,
                                display: "inline-flex", alignItems: "center", justifyContent: "center",
                                borderRadius: "8px",
                                fontSize: "12px",
                                fontWeight: p === currentPage ? 700 : 500,
                                cursor: p === currentPage ? "default" : "pointer",
                                userSelect: "none",
                                transition: "all 0.15s",
                                backgroundColor: p === currentPage ? "#1976d2" : "transparent",
                                color: p === currentPage ? "#fff" : "#555",
                                border: p === currentPage ? "1.5px solid #1976d2" : "1px solid transparent",
                                boxShadow: p === currentPage ? "0 2px 8px rgba(25,118,210,0.25)" : "none",
                                "&:hover": p !== currentPage ? {
                                    backgroundColor: "#f0f4ff",
                                    borderColor: "#c5d3f5",
                                    color: "#1976d2",
                                } : {},
                            }}
                        >
                            {p}
                        </Box>
                    )
                )}

                {/* Next */}
                <Tooltip title="Next page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === totalPages || totalPages === 0} onClick={() => onPageChange(currentPage + 1)} sx={navBtnSx(currentPage === totalPages || totalPages === 0)}>
                            <ChevronRightIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>

                {/* Last page */}
                <Tooltip title="Last page" arrow>
                    <span>
                        <IconButton size="small" disabled={currentPage === totalPages || totalPages === 0} onClick={() => onPageChange(totalPages)} sx={navBtnSx(currentPage === totalPages || totalPages === 0)}>
                            <LastPageIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
};

const CargoEscort = () => {
    const [cargoEscorts, setCargoEscorts] = useState([]);
    const [loading, setLoading]           = useState(true);
    const [error, setError]               = useState(null);
    const [searchTerm, setSearchTerm]     = useState("");
    const [lastUpdated, setLastUpdated]   = useState(null);

    // pagination state
    const [page, setPage]             = useState(1);
    const [limit, setLimit]           = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const normalizeCargoEscort = (item) => ({
        ...item,
        RequesterName:    item.requesterName    || item.RequesterName    || item.name        || "N/A",
        RequesterEmail:   item.requesterEmail   || item.RequesterEmail   || item.email       || "",
        RequesterPhoneNo: item.requesterPhone   || item.RequesterPhoneNo || item.phoneNumber || "N/A",
        receiverName:     item.receiverName     || item.ReceiverName     || item.receiver    || "N/A",
        receiverPhone:    item.receiverPhone    || item.ReceiverPhone    || "N/A",
        cargoType:        item.cargoType        || item.CargoType        || "N/A",
        pickupLocation:   item.pickupLocation   || item.PickupLocation   || item.from        || "N/A",
        deliveryLocation: item.deliveryLocation || item.DeliveryLocation || item.to          || "N/A",
        vehicleNumber:    item.vehicleNumber    || item.VehicleNumber    || item.vehicle     || "N/A",
        cargoValue:       item.cargoValue       || item.CargoValue       || "0",
        departureDate:    item.departureDate    || item.DepartureDate    || null,
        status:           item.status           || item.Status           || "N/A",
    });

    const fetchCargoEscorts = useCallback(async (pageNum, limitNum) => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.get(`/cargo-escort?page=${pageNum}&limit=${limitNum}`);

            const records = Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response?.cargoEscorts)
                ? response.cargoEscorts
                : Array.isArray(response?.result)
                ? response.result
                : Array.isArray(response)
                ? response
                : [];

            setCargoEscorts(records.map(normalizeCargoEscort));
            setTotalCount(response?.totalCount ?? records.length);
            setTotalPages(response?.totalPages ?? 1);
            setLastUpdated(new Date().toLocaleString());
        } catch (err) {
            setError(err?.message || "Unable to load cargo escort data.");
            setCargoEscorts([]);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleStatusChange = (id, newStatus) => {
        setCargoEscorts((prev) =>
            prev.map((c) => (c._id === id ? { ...c, status: newStatus } : c))
        );
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchCargoEscorts(newPage, limit);
    };

    const handleLimitChange = (newLimit) => {
        setLimit(newLimit);
        setPage(1);
        fetchCargoEscorts(1, newLimit);
    };

    useEffect(() => {
        fetchCargoEscorts(1, 10);

        socketService.initializeSocket()
            .then(() => {
                console.log("SOCKET CONNECTED");
                socketService.on("cargoescortinit", () => {
                    fetchCargoEscorts(page, limit);
                });
            })
            .catch((err) => console.log("SOCKET ERROR =>", err));

        return () => {
            socketService.off("cargoescortinit");
        };
    }, []);

    const filteredCargoEscorts = useMemo(() => {
        const term = searchTerm.toLowerCase();
        if (!term) return cargoEscorts;
        return cargoEscorts.filter((cargo) =>
            [
                cargo.RequesterName, cargo.RequesterEmail, cargo.RequesterPhoneNo,
                cargo.receiverName,  cargo.receiverPhone,  cargo.cargoType,
                cargo.status,        cargo.pickupLocation, cargo.deliveryLocation,
                cargo.vehicleNumber,
            ]
                .map((v) => String(v || "").toLowerCase())
                .some((v) => v.includes(term))
        );
    }, [cargoEscorts, searchTerm]);

    const columns = [
        { label: "#",                  width: 52  },
        { label: "Requester",          width: 160 },
        { label: "Contact",            width: 190 },
        { label: "Receiver",           width: 150 },
        { label: "Receiver Phone",     width: 150 },
        { label: "Cargo Type",         width: 130 },
        { label: "Pickup Location",    width: 180 },
        { label: "Delivery Location",  width: 180 },
        { label: "Vehicle No.",        width: 130 },
        { label: "Cargo Value",        width: 130 },
        { label: "Departure",          width: 120 },
        { label: "Status",             width: 130 },
        { label: "Actions",            width: 190 },
    ];

    return (
        <DashboardLayout>
            <MDBox pt={7} pb={3} sx={{ width: "100%", overflowX: "hidden" }}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card sx={{ overflow: "hidden", width: "100%", borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.07)" }}>

                            {/* Header */}
                            <MDBox
                                mx={2} mt={3} py={3} px={2}
                                variant="gradient" bgColor="info"
                                borderRadius="lg" coloredShadow="info"
                                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                            >
                                <MDTypography variant="h5" color="white" fontWeight="bold">
                                    Cargo Escort Management
                                </MDTypography>
                                <MDTypography variant="button" color="white" fontWeight="regular" sx={{ opacity: 0.9 }}>
                                    View all cargo escort requests and manage their status from the API.
                                </MDTypography>
                            </MDBox>

                            <Box px={3} py={3} sx={{ width: "100%", overflow: "hidden" }}>

                                {/* Toolbar */}
                                <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={2} mb={2}>
                                    <TextField
                                        label="Search"
                                        placeholder="Search by name, location, vehicle..."
                                        variant="outlined"
                                        size="small"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon sx={{ color: "#aaa", fontSize: 18 }} />
                                                </InputAdornment>
                                            ),
                                        }}
                                        sx={{ width: 320, minWidth: 220 }}
                                    />
                                    <Box display="flex" alignItems="center" gap={2}>
                                        <Typography variant="caption" color="textSecondary">
                                            {lastUpdated ? `Last updated: ${lastUpdated}` : "Not loaded yet"}
                                        </Typography>
                                        <MDButton variant="gradient" color="info" size="small" onClick={() => fetchCargoEscorts(page, limit)}>
                                            Refresh
                                        </MDButton>
                                    </Box>
                                </Box>

                                {/* States */}
                                {loading ? (
                                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={240} gap={2}>
                                        <Box sx={{
                                            width: 40, height: 40, border: "3px solid #e0e0e0",
                                            borderTop: "3px solid #4CAF50", borderRadius: "50%",
                                            animation: "spin 0.8s linear infinite",
                                            "@keyframes spin": { "0%": { transform: "rotate(0deg)" }, "100%": { transform: "rotate(360deg)" } },
                                        }} />
                                        <Typography sx={{ color: "#aaa", fontSize: "14px" }}>Loading cargo escorts...</Typography>
                                    </Box>
                                ) : error ? (
                                    <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" minHeight={240} gap={2}>
                                        <Typography color="error" sx={{ fontSize: "14px" }}>{error}</Typography>
                                        <MDButton variant="gradient" color="success" size="small" onClick={() => fetchCargoEscorts(page, limit)}>
                                            Retry
                                        </MDButton>
                                    </Box>
                                ) : filteredCargoEscorts.length === 0 ? (
                                    <EmptyData />
                                ) : (
                                    <>
                                        <TableContainer
                                            component={MDBox}
                                            sx={{
                                                boxShadow: "none", width: "100%",
                                                overflowX: "auto", borderRadius: "12px",
                                                border: "1px solid #eef0f4",
                                            }}
                                        >
                                            <Table
                                                size="small"
                                                stickyHeader
                                                sx={{
                                                    minWidth: columns.reduce((sum, col) => sum + col.width, 0),
                                                    tableLayout: "fixed",
                                                }}
                                                aria-label="cargo escort table"
                                            >
                                                <TableHead>
                                                    <TableRow>
                                                        {columns.map((col) => (
                                                            <TableCell
                                                                key={col.label}
                                                                sx={{
                                                                    fontWeight: 700, fontSize: "12px", color: "#555",
                                                                    textTransform: "uppercase", letterSpacing: "0.5px",
                                                                    padding: "12px 14px", width: col.width, minWidth: col.width,
                                                                    whiteSpace: "nowrap", backgroundColor: "#f4f6f9",
                                                                }}
                                                            >
                                                                {col.label}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                </TableHead>

                                                <TableBody>
                                                    {filteredCargoEscorts.map((cargo, index) => (
                                                        <TableRow
                                                            key={cargo._id || index}
                                                            hover
                                                            sx={{
                                                                transition: "background 0.15s",
                                                                "&:hover": { backgroundColor: "#f7fbff" },
                                                                "&:nth-of-type(even)": { backgroundColor: "#fafbfc" },
                                                                "&:nth-of-type(even):hover": { backgroundColor: "#f7fbff" },
                                                            }}
                                                        >
                                                            {/* # */}
                                                            <TableCell>
                                                                <Box sx={{
                                                                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                                                                    width: 26, height: 26, borderRadius: "50%",
                                                                    backgroundColor: "#f0f4ff", color: "#5c7cfa",
                                                                    fontSize: "11px", fontWeight: 700,
                                                                }}>
                                                                    {(page - 1) * limit + index + 1}
                                                                </Box>
                                                            </TableCell>

                                                            {/* Requester */}
                                                            <TableCell sx={{ padding: "12px 14px" }}>
                                                                <Typography sx={{ fontSize: "13px", fontWeight: 600, color: "#222" }}>
                                                                    {cargo.RequesterName}
                                                                </Typography>
                                                            </TableCell>

                                                            {/* Contact */}
                                                            <TableCell sx={{ padding: "12px 14px" }}>
                                                                <Typography sx={{ fontSize: "13px", fontWeight: 500, color: "#333" }}>
                                                                    {cargo.RequesterPhoneNo}
                                                                </Typography>
                                                                {cargo.RequesterEmail && (
                                                                    <Typography sx={{ fontSize: "11px", color: "#999", mt: "1px" }}>
                                                                        {cargo.RequesterEmail}
                                                                    </Typography>
                                                                )}
                                                            </TableCell>

                                                            {/* Receiver */}
                                                            <TableCell sx={{ padding: "12px 14px" }}>
                                                                <Typography sx={{ fontSize: "13px", fontWeight: 500 }}>
                                                                    {cargo.receiverName}
                                                                </Typography>
                                                            </TableCell>

                                                            {/* Receiver Phone */}
                                                            <TableCell sx={{ padding: "12px 14px", color: "#555" }}>{cargo.receiverPhone}</TableCell>

                                                            {/* Cargo Type */}
                                                            <TableCell sx={{ padding: "12px 14px" }}>
                                                                <Box sx={{
                                                                    display: "inline-block", px: 1.5, py: 0.4,
                                                                    borderRadius: "8px", backgroundColor: "#f0f4ff",
                                                                    color: "#3d5af1", fontSize: "12px", fontWeight: 600,
                                                                }}>
                                                                    {cargo.cargoType}
                                                                </Box>
                                                            </TableCell>

                                                            {/* Pickup Location */}
                                                            <TableCell title={cargo.pickupLocation} sx={{ padding: "12px 14px", color: "#444", whiteSpace: "nowrap" }}>
                                                                {cargo.pickupLocation}
                                                            </TableCell>

                                                            {/* Delivery Location */}
                                                            <TableCell title={cargo.deliveryLocation} sx={{ padding: "12px 14px", color: "#444", whiteSpace: "nowrap" }}>
                                                                {cargo.deliveryLocation}
                                                            </TableCell>

                                                            {/* Vehicle */}
                                                            <TableCell sx={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                                                                <Typography sx={{
                                                                    fontSize: "12px", fontFamily: "monospace", color: "#555",
                                                                    backgroundColor: "#f5f5f5", px: 1, py: 0.3,
                                                                    borderRadius: "6px", display: "inline-block",
                                                                }}>
                                                                    {cargo.vehicleNumber}
                                                                </Typography>
                                                            </TableCell>

                                                            {/* Cargo Value */}
                                                            <TableCell sx={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                                                                <Typography sx={{ fontSize: "13px", fontWeight: 700, color: "#1a7a45" }}>
                                                                    ₦{Number(cargo.cargoValue || 0).toLocaleString()}
                                                                </Typography>
                                                            </TableCell>

                                                            {/* Departure */}
                                                            <TableCell sx={{ padding: "12px 14px", color: "#666", fontSize: "12px", whiteSpace: "nowrap" }}>
                                                                {cargo.departureDate
                                                                    ? new Date(cargo.departureDate).toLocaleDateString("en-GB", {
                                                                        day: "2-digit", month: "short", year: "numeric",
                                                                    })
                                                                    : "—"}
                                                            </TableCell>

                                                            {/* Status */}
                                                            <TableCell sx={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                                                                <StatusBadge status={cargo.status} />
                                                            </TableCell>

                                                            {/* Actions */}
                                                            <TableCell sx={{ padding: "12px 14px", whiteSpace: "nowrap" }}>
                                                                <ActionButtons cargo={cargo} onStatusChange={handleStatusChange} />
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>

                                        {/* ── Pagination Bar ── */}
                                        <PaginationBar
                                            currentPage={page}
                                            totalPages={totalPages}
                                            totalCount={totalCount}
                                            limit={limit}
                                            onPageChange={handlePageChange}
                                            onLimitChange={handleLimitChange}
                                        />
                                    </>
                                )}
                            </Box>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
};

export default CargoEscort;